import React, { useEffect } from "react";
import { usePrimaryFetchedData } from "../../context/PrimaryFetchedDataContext";
import { useUserControls } from "../../context/UserControlsProvider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import { styled as muiStyled } from "@mui/material/styles";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Checkbox, Menu, MenuItem, Chip, Box } from "@mui/material";
import Divider from "@mui/material/Divider";
import { ItemSnapshot, ItemStatus } from "../../types/DB2";
import { getSuccessStatuses } from "../../util/util";
import { green, grey, orange } from "@mui/material/colors";
import WarningIcon from "@mui/icons-material/Warning";
import CustomListItem from "./CustomListItem";
import { useOfflineSync, useOfflineStatusBadge } from "../../context/OfflineSyncProvider";
import { aggregateOrderOfflineStatus, OfflineSyncInfo } from "../../offline";
import { isVerboseLoggingEnabled } from "../../utils/debug";

// Compare items by cabinet number (numeric rank), then by cabinet label, then by id
const cabinetRank = (value?: string | null) => {
  const s = (value ?? "").toString().trim();
  if (!s) return Number.POSITIVE_INFINITY;
  const matches = s.match(/\d+/g);
  if (!matches) return Number.POSITIVE_INFINITY;
  const nums = matches
    .map((seg) => parseInt(seg, 10))
    .filter((n) => Number.isFinite(n));
  return nums.length ? Math.min(...nums) : Number.POSITIVE_INFINITY;
};
const compareByCabinet = (a: ItemSnapshot, b: ItemSnapshot) => {
  const ar = cabinetRank((a as any)?.cabinetNumber);
  const br = cabinetRank((b as any)?.cabinetNumber);
  if (ar !== br) return ar - br;
  const as = ((a as any)?.cabinetNumber ?? "").toString();
  const bs = ((b as any)?.cabinetNumber ?? "").toString();
  if (as !== bs) return as.localeCompare(bs);
  return (a.id || "").localeCompare(b.id || "");
};

interface ExpandedState {
  panelOpen: boolean;
  orders: Record<string, boolean>;
  items: Record<string, boolean>;
}
interface GroupPanelCollapseContentProps {
  orderId: string;
  items: (ItemSnapshot & { offline?: OfflineSyncInfo })[];
  isGroupProcessed: boolean;
  onGroupClick: (e: React.MouseEvent<HTMLElement>, orderId: string) => void;
  isOpen: boolean;
  showCheckbox: boolean;
  selected: boolean;
  indeterminate: boolean;
  onSelect: (orderId: string, checked: boolean) => void;
  children: React.ReactNode;
  onWarningClick?: (e: React.MouseEvent<HTMLElement>) => void;
  offlineStatus?: OfflineSyncInfo;
}
const GroupPanelCollapseContent: React.FC<GroupPanelCollapseContentProps> = ({
  orderId,
  items,
  isGroupProcessed,
  onGroupClick,
  isOpen,
  showCheckbox,
  selected,
  indeterminate,
  onSelect,
  children,
  onWarningClick,
  offlineStatus,
}) => {
  const { loadingStatus } = useUserControls();
  const verbose = isVerboseLoggingEnabled();
  const successStatuses = getSuccessStatuses(loadingStatus);
  const allItemsSuccess =
    items.length > 0 &&
    items.every((it: any) => successStatuses.includes(it.status));
  const bgColor = isGroupProcessed ? green[50] : grey[100];
  const color = isGroupProcessed ? green[900] : grey[800];
  const offlineBadge = useOfflineStatusBadge(offlineStatus?.status);
  const { syncNow } = useOfflineSync();
  const offlineStatusCode = offlineStatus?.status;
  const isRetryableOfflineStatus =
    offlineStatusCode === "pending" || offlineStatusCode === "error";
  const handleOfflineChipClick = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
      if (!isRetryableOfflineStatus) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const status = offlineStatusCode ?? "unknown";
      try {
        console.log("[OfflineRetry] User tapped order offline chip", {
          scope: "order",
          orderId,
          status,
          itemsInOrder: items?.length ?? 0,
        });
        await syncNow();
        console.log("[OfflineRetry] syncNow resolved after order chip tap", {
          orderId,
          status,
        });
      } catch (error) {
        console.error("[OfflineRetry] syncNow rejected after order chip tap", {
          orderId,
          status,
          error,
        });
      }
    },
    [isRetryableOfflineStatus, items?.length, offlineStatusCode, orderId, syncNow]
  );
  // derive display order number (strip prefix when present)
  const firstItem = items && items.length ? (items[0] as any) : undefined;
  const prefixFromItem: string | undefined = firstItem?.prefix;
  const orderNoFromItem: string | undefined = firstItem?.orderNumber;
  let displayOrderNo: string = orderId;
  if (typeof orderNoFromItem === 'string' && orderNoFromItem.length) {
    displayOrderNo = orderNoFromItem;
  } else if (
    typeof prefixFromItem === 'string' &&
    typeof orderId === 'string' &&
    orderId.startsWith(prefixFromItem)
  ) {
    displayOrderNo = orderId.slice(prefixFromItem.length);
  } else if (typeof orderId === 'string') {
    displayOrderNo = orderId.replace(/^[A-Za-z]+-/, '');
  }
  if (verbose) {
    try {
      console.log('[GroupPanel] order label sources', {
        orderId,
        prefixFromItem,
        orderNoFromItem,
        displayOrderNo,
        itemsCount: items?.length ?? 0,
      });
    } catch {}
  }

  return (
    <React.Fragment key={orderId}>
      {/* Order-level checkbox */}
      <ListItem
        sx={(theme) => ({
          cursor: "pointer",
          alignItems: "center",
          paddingTop: ".25rem",
          paddingBottom: ".25rem",
          // @ts-ignore
          bgcolor: bgColor,
          ml: "0",
          pl: 3, // indent orders a bit under the panel header
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          position: 'sticky',
          top: 'var(--panel-header-offset, 56px)', // below panel header
          zIndex: 1,
        })}
        onClick={(e) => onGroupClick(e, orderId)}
      >
        {isOpen ? (
          <ExpandLess sx={{ color: color }} />
        ) : (
          <ExpandMore sx={{ color: color }} />
        )}
        <ListItemIcon
          sx={{
            opacity: showCheckbox ? 1 : 0,
            width: showCheckbox ? 30 : 0,
            overflow: "hidden",
            minWidth: 0,
            marginLeft: ".55rem",
            transition: "width .3s ease, opacity .3s ease",
          }}
        >
          <Checkbox
            color="default"
            edge="start"
            checked={selected}
            indeterminate={indeterminate}
            onClick={(e) => {
              e.stopPropagation();
              const next = !(selected && !indeterminate);
              onSelect(orderId, next);
            }}
          />
        </ListItemIcon>
        <ListItemText
          primary={`Order: ${displayOrderNo} (${items.length}):`}
          sx={{ color: allItemsSuccess ? "success.main" : "inherit" }}
        />
        {offlineBadge &&
          (isRetryableOfflineStatus ? (
            <Box
              component="button"
              type="button"
              onClick={handleOfflineChipClick}
              sx={{
                ml: 1,
                border: "none",
                padding: 0,
                background: "transparent",
                display: "inline-flex",
                cursor: "pointer",
              }}
              aria-label="Retry offline sync"
              title={offlineStatus?.lastError}
            >
              <Chip
                label={offlineBadge.label}
                color={offlineBadge.color as "success" | "warning" | "error"}
                size="small"
                variant={offlineBadge.color === "success" ? "outlined" : "filled"}
                sx={{ pointerEvents: "none" }}
              />
            </Box>
          ) : (
            <Chip
              label={offlineBadge.label}
              color={offlineBadge.color as "success" | "warning" | "error"}
              size="small"
              variant={offlineBadge.color === "success" ? "outlined" : "filled"}
              sx={{ ml: 1 }}
              title={offlineStatus?.lastError}
            />
          ))}
        {!allItemsSuccess && isGroupProcessed && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onWarningClick && onWarningClick(e);
            }}
            sx={{ ml: 0.5 }}
            aria-label="warnings"
          >
            <WarningIcon
              sx={{
                color: orange[300],
                fontSize: "1.25rem",
                opacity: 0.8,
              }}
            />
          </IconButton>
        )}
      </ListItem>

      <Collapse
        in={isOpen}
        timeout="auto"
        unmountOnExit
        style={{ backgroundColor: bgColor, marginLeft: "0" }}
      >
        {children}
      </Collapse>
    </React.Fragment>
  );
};

const OrderGroupsPanel: React.FC<{
  title: string;
  groups: Record<string, any[]>;
  selectedIds: string[];
  onToggleSelect: (ids: string | string[], options?: { select?: boolean }) => void;
  initiallyOpen?: boolean;
  successStatuses: ItemStatus[];
  startStatuses?: ItemStatus[];
  onUpdateStatusClick: (item: ItemSnapshot) => void;
  onResetItemClick?: (item: ItemSnapshot) => void;
  isGroupsProcessed: boolean;
  allItemsSuccess?: boolean;
  anyNotProcessed?: boolean;
}> = ({
  onResetItemClick,
  isGroupsProcessed,
  title,
  groups,
  selectedIds,
  onToggleSelect,
  initiallyOpen = true,
  successStatuses,
  startStatuses,
  onUpdateStatusClick,
  allItemsSuccess = false,
  anyNotProcessed = false,
}) => {
  // pull context
  const { shipments } = usePrimaryFetchedData();
  const { selectedShipmentObject } = useUserControls();
  const { decorateItems } = useOfflineSync();
  const verbose = isVerboseLoggingEnabled();

  // state
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [singleItemToUpdate, setSingleItemToUpdate] =
    React.useState<ItemSnapshot | null>(null);
  const [expandedState, setExpandedState] = React.useState<ExpandedState>(
    () => ({
      panelOpen: initiallyOpen,
      orders: {},
      items: {},
    })
  );
  const [warningSnackbarOpen, setWarningSnackbarOpen] = React.useState(false);

  // derived
  const allItems = Object.values(groups).flat();
  const isAllItemsSuccess = (items: ItemSnapshot[]) =>
    items.every((item) => successStatuses.includes(item.status));
  const allOrdersSuccess = isAllItemsSuccess(allItems);
  // Extra diagnostics removed

  // effects

  // effect manually adjusting expandedState when data is updated
  useEffect(() => {
    if (!selectedShipmentObject) return;
    const newExpandedState = { ...expandedState };
    if (isGroupsProcessed) {
      const isEmpty = Object.values(groups).length === 0;
      if (isEmpty) {
        newExpandedState.panelOpen = false;
      }
      // Always default processed panel to collapsed unless user expands it
      if (allItemsSuccess && !anyNotProcessed) {
        newExpandedState.panelOpen = false;
      }
      // Do not auto-open processed panel; default each order collapsed unless previously toggled
      Object.entries(groups).forEach(([orderId]) => {
        if (typeof newExpandedState.orders[orderId] === 'undefined') {
          newExpandedState.orders[orderId] = false;
        }
      });
    } else {
      newExpandedState.panelOpen = true;
      Object.entries(groups).forEach(([orderId, items]) => {
        newExpandedState.orders[orderId] = true;
      });
    }
    setExpandedState(newExpandedState);
  }, [shipments, groups, isGroupsProcessed, allItemsSuccess, anyNotProcessed]);

  // helpers

  // expanded state helpers
  const setPanelOpen = (open: boolean) =>
    setExpandedState((s) => ({ ...s, panelOpen: open }));
  const togglePanel = () => setPanelOpen(!expandedState.panelOpen);
  const setOrderOpen = (orderId: string, open: boolean) =>
    setExpandedState((s) => ({
      ...s,
      orders: { ...s.orders, [orderId]: open },
    }));
  const toggleOrder = (orderId: string) =>
    setExpandedState((s) => ({
      ...s,
      orders: { ...s.orders, [orderId]: !s.orders[orderId] },
    }));
  const setItemOpen = (itemId: string, open: boolean) =>
    setExpandedState((s) => ({ ...s, items: { ...s.items, [itemId]: open } }));
  const toggleItem = (itemId: string) =>
    setExpandedState((s) => ({
      ...s,
      items: { ...s.items, [itemId]: !s.items[itemId] },
    }));

  const openItemMenu = (
    e: React.MouseEvent<HTMLElement>,
    item: ItemSnapshot
  ) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setSingleItemToUpdate(item);
  };
  const closeItemMenu = () => {
    setMenuAnchor(null);
    setSingleItemToUpdate(null);
  };

  // handlers
  const handleUpdateStatusClick = (item: ItemSnapshot) => {
    onUpdateStatusClick?.(item);
  };
  const handleResetItemClick = (item: ItemSnapshot) => {
    onResetItemClick?.(item);
  };
  const handleSelectClick = (
    itemSnapshot: ItemSnapshot,
    desiredState?: boolean
  ) => {
    if (verbose) {
      try {
        console.debug("[GroupPanel] handleSelectClick", {
          orderId: itemSnapshot?.orderId,
          itemId: itemSnapshot?.id,
          desiredState,
        });
      } catch (_err) {}
    }
    const shouldSelect =
      typeof desiredState === "boolean"
        ? desiredState
        : !selectedIds.includes(itemSnapshot.id);
    onToggleSelect(itemSnapshot.id, { select: shouldSelect });
  };
  const handleItemClick = (
    e: React.MouseEvent<HTMLElement>,
    item: ItemSnapshot
  ) => {
    e.stopPropagation();
    toggleItem(item.id);
  };
  const handleGroupClick = (
    e: React.MouseEvent<HTMLElement>,
    orderId: string
  ) => {
    e.stopPropagation();
    toggleOrder(orderId);
  };
  const handleOptionsClick = (
    e: React.MouseEvent<HTMLElement>,
    item: ItemSnapshot
  ) => {
    e.stopPropagation();
    openItemMenu(e, item);
  };

  const modalContainer = React.useCallback(() => {
    if (typeof document === "undefined") {
      return null;
    }
    const host = document.querySelector("ion-app") as HTMLElement | null;
    return host ?? document.body ?? null;
  }, []);

  const renderItemsForOrder = (orderId: string, items: (ItemSnapshot & { offline?: OfflineSyncInfo })[]) => (
    <List component="div" disablePadding>
      {items.map((item, index) => {
        if (verbose) {
          try {
            console.debug("[GroupPanel] render item snapshot", {
              orderId,
              itemId: item?.id,
              itemOrderId: (item as any)?.orderId,
            });
          } catch (_err) {}
        }
        return (
          <CustomListItem
            key={item.id}
            itemSnapshot={item}
            index={index}
            onOptionsClick={handleOptionsClick}
            onItemClick={handleItemClick}
            selected={selectedIds.includes(item.id)}
            onSelect={(targetItem, checked) =>
              handleSelectClick(targetItem, checked)
            }
            isExpanded={expandedState.items[item.id]}
          />
        );
      })}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={(e: any) => {
          e.stopPropagation();
          closeItemMenu();
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        keepMounted
        slotProps={{ root: { container: modalContainer } }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            if (singleItemToUpdate) {
              const nextState = !selectedIds.includes(singleItemToUpdate.id);
              handleSelectClick(singleItemToUpdate, nextState);
            }
            closeItemMenu();
          }}
        >
          Select
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            if (singleItemToUpdate) handleUpdateStatusClick(singleItemToUpdate);
            closeItemMenu();
          }}
        >
          Update Status
        </MenuItem>
        {isGroupsProcessed && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              if (singleItemToUpdate) handleResetItemClick(singleItemToUpdate);
              closeItemMenu();
            }}
          >
            Reset
          </MenuItem>
        )}
      </Menu>
    </List>
  );

  const panelBgColor = isGroupsProcessed ? green[100] : grey[200];
  const panelColor = isGroupsProcessed ? green[900] : grey[800];

  const allItemsSelected =
    allItems.every((item: any) => selectedIds.includes(item.id)) &&
    allItems.length > 0;
  const someItemsSelected = allItems.some((item: any) =>
    selectedIds.includes(item.id)
  );
  const allIds = allItems.map((i: any) => i.id);

  const headerRef = React.useRef<HTMLLIElement | null>(null);

  React.useEffect(() => {
    const updatePanelHeaderOffset = () => {
      try {
        const h = headerRef.current?.offsetHeight ?? 56;
        document.documentElement.style.setProperty('--panel-header-offset', `${h}px`);
      } catch (_) {
        // ignore
      }
    };
    updatePanelHeaderOffset();
    window.addEventListener('resize', updatePanelHeaderOffset);
    return () => window.removeEventListener('resize', updatePanelHeaderOffset);
  }, [expandedState.panelOpen]);

  return (
    <>
      <ListItem
        ref={headerRef}
        onClick={() => togglePanel()}
        sx={{
          cursor: "pointer",
          alignItems: "center",
          bgcolor: panelBgColor,
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          position: 'sticky',
          top: 0,
          zIndex: 2,
        }}
      >
        {expandedState.panelOpen ? (
          <ExpandLess sx={{ color: panelColor }} />
        ) : (
          <ExpandMore sx={{ color: panelColor }} />
        )}
        <ListItemIcon
          sx={{
            opacity: selectedIds.length > 0 ? 1 : 0,
            width: selectedIds.length > 0 ? 30 : 0,
            overflow: "hidden",
            minWidth: 0,
            marginLeft: ".55rem",
            transition: "width .3s ease, opacity .3s ease",
          }}
        >
          <Checkbox
            color="default"
            edge="start"
            checked={allItemsSelected}
            indeterminate={someItemsSelected && !allItemsSelected}
            onClick={(e) => {
              e.stopPropagation();
              const next = !(allItemsSelected && !someItemsSelected);
              onToggleSelect(allIds, { select: next });
            }}
          />
        </ListItemIcon>
        <ListItemText
          primary={title}
          sx={{
            color: panelColor,
          }}
        />
        {!allOrdersSuccess && isGroupsProcessed && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setWarningSnackbarOpen(true);
            }}
            aria-label="warnings"
          >
            <WarningIcon
              sx={{
                color: orange[300],
                fontSize: "1.25rem",
                ml: 1,
                opacity: 0.8,
              }}
            />
          </IconButton>
        )}
      </ListItem>
      <Collapse
        in={expandedState.panelOpen}
        timeout="auto"
        unmountOnExit
        style={{ backgroundColor: panelBgColor }}
      >
        {Object.keys(groups).length === 0 ? (
          <></>
        ) : (
          Object.entries(groups)
            .sort(([aOrderId, aItems], [bOrderId, bItems]) => {
              const aAllSuccess = aItems.every((item: any) =>
                successStatuses.includes(item.status)
              );
              const bAllSuccess = bItems.every((item: any) =>
                successStatuses.includes(item.status)
              );
              if (aAllSuccess && !bAllSuccess) return 1;
              if (!aAllSuccess && bAllSuccess) return -1;
              return 0;
            })
            .map(([orderId, items]) => {
              const decoratedItems = decorateItems(items as ItemSnapshot[], "item");
              decoratedItems.sort(compareByCabinet);
              const offlineInfos = decoratedItems
                .map((item) => item.offline)
                .filter((info): info is OfflineSyncInfo => Boolean(info));
              const orderOfflineStatus = aggregateOrderOfflineStatus(offlineInfos);
              const allItemsSelected =
                decoratedItems.length > 0 &&
                decoratedItems.every((it: any) => selectedIds.includes(it.id));
              const someItemsSelected =
                decoratedItems.length > 0 &&
                decoratedItems.some((it: any) => selectedIds.includes(it.id));
              const allIds = decoratedItems.map((it: any) => it.id);
              return decoratedItems.length > 0 ? (
                <GroupPanelCollapseContent
                  key={orderId}
                  orderId={orderId}
                  items={decoratedItems}
                  isGroupProcessed={isGroupsProcessed}
                  onGroupClick={(e) => handleGroupClick(e, orderId)}
                  isOpen={expandedState.orders[orderId]}
                  showCheckbox={selectedIds.length > 0}
                  selected={allItemsSelected}
                  indeterminate={!allItemsSelected && someItemsSelected}
                  onSelect={(orderId, checked) => {
                    onToggleSelect(allIds, { select: checked });
                  }}
                  onWarningClick={() => setWarningSnackbarOpen(true)}
                  offlineStatus={orderOfflineStatus}
                >
                  {renderItemsForOrder(orderId, decoratedItems)}
                </GroupPanelCollapseContent>
              ) : (
                <></>
              );
            })
        )}
      </Collapse>
      <Divider />
      <WarningSnackbar
        open={warningSnackbarOpen}
        autoHideDuration={2500}
        onClose={() => setWarningSnackbarOpen(false)}
        message="warning items missing or damaged."
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default OrderGroupsPanel;
const WarningSnackbar = muiStyled(Snackbar)(() => ({
  "& .MuiSnackbarContent-root": {
    marginBottom:
      "var(--mobile-bottom-toolbar-clearance, 96px) !important",
  },
}));
